// Display-only subject normalisation for admin lists/analytics — mirrors the
// backend utils/subjectName.js. Subject strings are free-typed across the app
// (teacher builder, AI generation, old imports) with no enforced casing, so the
// same real subject shows up as "maths" AND "Mathematics", or with corrupt
// values like "20(mod" from a bad question import. These helpers give the admin
// UI a clean, de-duplicated, Title-Cased view regardless of what the API sends
// (and independent of whether the backend has been redeployed yet).
//
// Safe to normalise here because these are read-only aggregations for display —
// nothing is sent back as an exact-match filter, so canonicalising the label
// can't desync from stored rows the way it would in a picker.

const CANONICAL_SUBJECTS = [
    "Physics", "Chemistry", "Biology", "Further Mathematics", "Mathematics",
    "Agricultural Science", "Commerce", "CRS", "Christian Religious Studies (CRS)",
    "English Language", "English Studies", "Basic Science", "Basic Technology",
    "Social Studies", "Civic Education", "Computer Science",
    "Physical and Health Education", "Physical and Health Education (PHE)",
    "Business Studies", "Creative Arts", "Home Economics", "Security Education",
    "Literature", "Geography", "History", "Economics", "Government", "French",
    "Arabic", "Hausa", "Igbo", "Yoruba", "Fine Arts", "Music",
    "Technical Drawing", "Auto Mechanics", "Electronics", "Building Construction",
    "Metal Work", "Wood Work", "Food and Nutrition", "Clothing and Textiles",
];

const CANONICAL_BY_LOWER = new Map(CANONICAL_SUBJECTS.map((n) => [n.toLowerCase(), n]));

const SUBJECT_ALIASES = {
    maths: "Mathematics", math: "Mathematics", mathematic: "Mathematics",
    "further maths": "Further Mathematics", "further math": "Further Mathematics",
    "f/maths": "Further Mathematics", "additional mathematics": "Further Mathematics",
    civic: "Civic Education", civics: "Civic Education", "civic edu": "Civic Education",
    computer: "Computer Science", "computer studies": "Computer Science", computing: "Computer Science",
    econs: "Economics", econ: "Economics", govt: "Government",
    english: "English Language", "english lang": "English Language", "use of english": "English Language",
    lit: "Literature", "literature in english": "Literature", "literature-in-english": "Literature",
    agric: "Agricultural Science", agriculture: "Agricultural Science", "agric science": "Agricultural Science",
    bio: "Biology", chem: "Chemistry", phy: "Physics", geo: "Geography",
    phe: "Physical and Health Education", "physical education": "Physical and Health Education",
    crk: "CRS", "christian religious knowledge": "CRS", "christian religious studies": "CRS",
};

const LOWERCASE_WORDS = new Set(["and", "of", "the", "in", "for"]);

const titleCaseFallback = (raw) =>
    raw
        .split(/\s+/)
        .map((word, i) => {
            if (!word) return word;
            if (/^\(?[A-Z]{2,5}\)?$/.test(word)) return word;
            const lower = word.toLowerCase();
            if (i > 0 && LOWERCASE_WORDS.has(lower)) return lower;
            return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(" ");

export const normalizeSubjectName = (raw) => {
    if (!raw) return raw;
    const trimmed = String(raw).trim();
    if (!trimmed) return trimmed;
    const lower = trimmed.toLowerCase();
    return CANONICAL_BY_LOWER.get(lower) || SUBJECT_ALIASES[lower] || titleCaseFallback(trimmed);
};

export const isDisplayableSubject = (raw) => {
    if (!raw) return false;
    const s = String(raw).trim();
    if (s.length < 2) return false;
    if (/\d/.test(s)) return false;
    if (!/[A-Za-z]{2,}/.test(s)) return false;
    return /^[A-Za-z][A-Za-z /()&'.-]*$/.test(s);
};

// Merge an array of { subject, attempts, accuracy } rows so casing/alias
// variants of one subject collapse into a single row with combined attempts and
// a recomputed accuracy. Corrupt subject values are dropped.
export const mergeSubjectPerformance = (rows = []) => {
    const byName = {};
    rows.forEach((r) => {
        if (!isDisplayableSubject(r.subject)) return;
        const subject = normalizeSubjectName(r.subject);
        const attempts = Number(r.attempts) || 0;
        // Reconstruct correct count from the row's own accuracy so we can
        // re-average correctly after merging (the API only returns accuracy).
        const correct = Math.round((Number(r.accuracy) || 0) / 100 * attempts);
        if (!byName[subject]) byName[subject] = { subject, attempts: 0, correct: 0 };
        byName[subject].attempts += attempts;
        byName[subject].correct += correct;
    });
    return Object.values(byName)
        .map((s) => ({
            subject: s.subject,
            attempts: s.attempts,
            accuracy: s.attempts ? Math.round((s.correct / s.attempts) * 100) : 0,
        }))
        .sort((a, b) => a.accuracy - b.accuracy);
};

// Merge the nested topics-overview payload (subject -> years[] -> topics[]) so
// casing/alias subject variants collapse into one entry (years merged by label,
// totals summed) and corrupt subjects are dropped.
export const mergeTopicsOverview = (subjects = []) => {
    const byName = {};
    subjects.forEach((s) => {
        if (!isDisplayableSubject(s.subject)) return;
        const name = normalizeSubjectName(s.subject);
        if (!byName[name]) byName[name] = { subject: name, total: 0, years: {} };
        const target = byName[name];
        (s.years || []).forEach((y) => {
            if (!target.years[y.yearLabel]) {
                target.years[y.yearLabel] = { yearLabel: y.yearLabel, source: y.source, total: 0, topics: [] };
            }
            target.years[y.yearLabel].total += Number(y.total) || 0;
            target.years[y.yearLabel].topics.push(...(y.topics || []));
        });
    });
    return Object.values(byName)
        .map((s) => {
            const years = Object.values(s.years)
                .map((y) => ({ ...y, topics: [...y.topics].sort((a, b) => b.total - a.total) }))
                .sort((a, b) => String(a.yearLabel).localeCompare(String(b.yearLabel)));
            return { subject: s.subject, total: years.reduce((acc, y) => acc + y.total, 0), years };
        })
        .sort((a, b) => a.subject.localeCompare(b.subject));
};
